/**
 * useMembership Hook
 *
 * Thin wrapper around useAuth() that provides membership-specific
 * derived state. Reads membershipStatus and icPrincipal directly
 * from AuthContext (populated by oracle-bridge session check on mount).
 *
 * Story: BL-011.5 — Frontend Membership Gating
 * Story: BL-030.1 — Migrate to useAuth() context
 * Story: BL-430 — gate on SBT membership_status, not PG roles[]
 * AC: 1, 6
 */

import { useAuth } from '@hello-world-dao/auth';

export type MembershipStatusValue =
  | 'Registered'
  | 'Active'
  | 'Inactive'
  | 'Expired'
  | 'Revoked'
  | null;

export interface UseMembershipResult {
  membershipStatus: MembershipStatusValue;
  isActiveMember: boolean;
  isRegistered: boolean;
  /**
   * bl-451b: true ONLY when membershipStatus === 'Inactive' (member soft-exit
   * / "stepped back"). Inactive is a non-Active, REVERSIBLE state — the SBT and
   * on-chain history persist, the member is auto-suppressed from the public
   * directory (enforced canister-side in `get_directory`), and governance
   * write-paths are gated. Distinct from Expired (dues lapse) and Revoked
   * (admin-terminal). The member reactivates via `reactivate()`.
   */
  isInactive: boolean;
  isLoading: boolean;
  icPrincipal: string | null;
}

function toMembershipStatus(raw: string | null | undefined): MembershipStatusValue {
  if (
    raw === 'Active' ||
    raw === 'Registered' ||
    raw === 'Inactive' ||
    raw === 'Expired' ||
    raw === 'Revoked'
  ) {
    return raw;
  }
  return null;
}

/**
 * Hook to get current user's membership status from the auth context.
 *
 * Returns:
 * - membershipStatus: "Registered" | "Active" | "Expired" | "Revoked" | null
 * - isActiveMember: true ONLY when membershipStatus === "Active"
 * - isRegistered: true when membershipStatus === "Registered"
 * - isLoading: true while auth session is being checked on mount
 * - icPrincipal: IC principal string or null if not linked
 *
 * BL-430 (BUG-DAO-010): isActiveMember is now derived strictly from the SBT
 * membership_status returned by the membership canister (via oracle-bridge
 * /api/auth/v2/login + /api/membership/status). The previous fallback to the
 * PostgreSQL `roles[]` array (treating roles.includes('member') as a positive
 * signal) was incorrect: the PG `roles[]` table models application-role
 * concerns (admin / developer / user) and ships every authenticated session
 * with `roles: ['member']` by default — so the fallback gated nothing and
 * surfaced creation CTAs to non-members.
 *
 * Defense-in-depth: the load-bearing API gate lives in oracle-bridge
 * (BL-427) which 403s non-Active users on POST /api/proposals, /api/discussions,
 * etc. This hook is the matching UI gate so non-members no longer see CTAs
 * that fail on click.
 */
export function useMembership(): UseMembershipResult {
  const { membershipStatus: rawStatus, icPrincipal, isLoading } = useAuth();
  const membershipStatus = toMembershipStatus(rawStatus);
  return {
    membershipStatus,
    isActiveMember: membershipStatus === 'Active',
    isRegistered: membershipStatus === 'Registered',
    isInactive: membershipStatus === 'Inactive',
    isLoading,
    icPrincipal: icPrincipal ?? null,
  };
}
